import type { ProductStatusEnum } from './ProductStatusEnum'
import type { ProductTypeEnum } from './ProductTypeEnum'
import { ProductCurrentInventory } from './ProductCurrentInventory'
import { ProductMetadata } from './ProductMetadata'
import { ProductBillOfMaterial } from './ProductBillOfMaterial'
import { Field, Float, ID, InputType, ObjectType } from 'type-graphql'
import { modelOptions, Prop } from '@typegoose/typegoose'
import { BaseEntity } from '../base/BaseEntity'
import { Blog } from '../blog/Blog'
import { ProductOperation } from './ProductOperation'
import { Asset } from '../asset/Asset'
import { ProductVendor } from './ProductVendor'
import GraphQLJSON from 'graphql-type-json'
import { Activity } from '../activity/Activity'
import { SerialNumberTypeEnum } from '../inventorybyserialnumber'
import { InventoryBySerialNumber } from '../inventorybyserialnumber'
import { generateEntityId } from '../generateEntityId'

@ObjectType()
@InputType('ProductInput')
@modelOptions({ schemaOptions: { timestamps: true } })
export class Product extends BaseEntity {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('prod') })
  declare _id: string

  @Field(() => String)
  @Prop({ type: String })
  declare status: ProductStatusEnum

  @Field(() => Float)
  @Prop({ type: Number })
  price: number

  @Field(() => String)
  @Prop({ type: String })
  coverImage: string

  /**
   * There is a resolver for this that automatically gets the thumbnail for the image width: 150px
   */
  @Field(() => String)
  coverImageThumb: string

  @Field(() => String)
  @Prop({ type: String })
  fullDescription: string

  @Field(() => Boolean)
  @Prop({ type: Boolean })
  forSale: boolean

  @Field(() => [Product])
  @Prop({ type: [String] }) // it will be stored as string in db
  relatedProducts: Product[]

  @Field(() => [Blog])
  @Prop({ type: [String] }) // it will be stored as string in db
  relatedPosts: Blog[]

  @Field(() => [Asset])
  @Prop({ type: [Asset] })
  assets: Asset[]

  @Field(() => [ProductMetadata])
  @Prop({ type: [ProductMetadata] })
  metadata: ProductMetadata[]

  @Field(() => [ProductBillOfMaterial])
  @Prop({ type: [ProductBillOfMaterial] })
  billOfMaterials: ProductBillOfMaterial[]

  //we need this so we can traverse to the deepest level, without deep traversal from graphql
  //it is just a FieldResolver that stringify the billOfMaterials field
  @Field(() => GraphQLJSON)
  fullBillOfMaterials: string

  @Field(() => ProductCurrentInventory)
  @Prop({ type: ProductCurrentInventory })
  inventory: ProductCurrentInventory

  @Field(() => Boolean)
  @Prop({ type: Boolean })
  isNewImport: boolean

  @Field(() => Date)
  @Prop({ type: Date })
  importedDate: Date

  @Field(() => String)
  @Prop({ type: String })
  harmonizedTariffCode: string

  @Field(() => Float)
  @Prop({ type: Number })
  minimumQuantityAllowance: number

  @Field(() => String)
  @Prop({ type: String })
  productType: ProductTypeEnum

  @Field(() => String)
  @Prop({ type: String })
  qboId: string

  @Field(() => Boolean)
  //this is computed value so there is no need to store in db
  inStock: boolean

  @Field(() => Float)
  //this is computed value so there is no need to store in db
  requiredComputedQuantityOnHand: number

  @Field(() => Float)
  //this is computed value so there is no need to store in db
  computedQuantityOnHand: number

  @Field(() => [ProductOperation])
  @Prop({ type: [ProductOperation] })
  operations: ProductOperation[]

  @Field(() => Float)
  @Prop({ type: Number })
  materialCost: number

  @Field(() => Boolean)
  @Prop({ type: Boolean })
  requiredSerialNumber: boolean

  /**
   * We need to defined the type of serial nummbering for this product, if it does not specify, it will default to Individual
   */
  @Field(() => String)
  @Prop({ type: String, default: SerialNumberTypeEnum.Individual })
  serialNumberType: SerialNumberTypeEnum

  //This has field resolver, but we put it here for type-safety too
  @Field(() => Boolean)
  hasSubComponents: boolean

  //This has field resolver, but we put it here for type-safety too
  //This is the   totalOperationsCost + material/miscellaneous cost + totalSubComponentsCost,
  @Field(() => Number)
  totalManufactureCost: number

  //TODO: This should have resolver too, but we put it here for type-safety too
  //This is just a sum of all operation cost
  @Field(() => Number)
  totalOperationsCost: number

  //TODO: This should have resolver too, but we put it here for type-safety too
  //This is the sum of all bill of materials (requried only) totalManufactureCost
  @Field(() => Number)
  totalSubComponentsCost: number

  @Field(() => [ProductVendor])
  @Prop({ type: [ProductVendor] })
  vendors: ProductVendor[]

  //This has field resolver, but we put it here for type-safety too
  //This is the is used on the product list that, that transform the array metadata to object
  //the design of metadata should be really an object, not an array so it' easier to query and update
  //maybe in the future we create a new field specification, and deprecate the metadata and metadataObject
  @Field(() => GraphQLJSON)
  metadataObject: Record<string, boolean | number | string>

  @Field(() => [Activity])
  @Prop({ type: [Activity] })
  remarksActivity: Activity[]

  @Field(() => Float)
  @Prop({ type: Number })
  originalPrice: number

  @Field(() => Float)
  discountPercentage: number

  @Field(() => [InventoryBySerialNumber])
  inventoryBySerialNumbers: ReturnType<() => InventoryBySerialNumber[]>
}
